<template>
    <div
        ref="containerRef"
        class="w-full h-full"
    >
        <redesign-base-chart
            v-if="hasData"
            class="w-full h-full"
            :widget="data"
            :option="chartOptions"
            :loading="isLoading"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useElementSize } from '@vueuse/core'
import RedesignBaseChart from '@/components/RedesignComponents/Charts/RedesignBaseChart.vue'
import { WidgetExtended } from '@/types/dashboard'
import { ChartOptions } from '@/types/echart'
import useWidgetDataWithRefreshInterval from '@/composables/useWidgetDataWithRefreshInterval'
import { selectedAccountBreaks } from '@/composables/globalData/useEntitiesData'
import { colorsForPieChartCallsPerAccColorsLayout } from '@/enum/colors'

interface GanttDataItem {
    x: string | number
    x2: string | number
    y: number
    cdr_type: string
    lable?: string
    color?: string
    [key: string]: unknown
}

interface XaxisChartApiData {
    GanttData?: GanttDataItem[]
    categories?: string[]
}

// Height (px) reserved for each category row — determines how many rows fit in the viewport
const ROW_HEIGHT = 25
// Fixed chrome heights: top margin + bottom legend area (15% of container)
const GRID_TOP = 40

const props = defineProps<{
    data: WidgetExtended
}>()

const containerRef = ref<HTMLElement | null>(null)
const { height: containerHeight } = useElementSize(containerRef)

const { data: loadedData, isLoading } = useWidgetDataWithRefreshInterval<
    XaxisChartApiData[],
    { data: WidgetExtended },
    'data'
>(props, 'data')

// Check if we have valid data
const hasData = computed(() => {
    if (!loadedData.value || !Array.isArray(loadedData.value) || loadedData.value.length === 0) {
        return false
    }

    const apiData = loadedData.value[0]
    return !!(apiData?.GanttData && apiData.GanttData.length > 0)
})


/**
 * How many category rows fit in the current container height.
 * Re-evaluates automatically whenever the widget is resized.
 */
const itemsPerPage = computed(() => {
    if (containerHeight.value <= 0) return 10
    const gridBottom = containerHeight.value * 0.15
    const availableGridHeight = containerHeight.value - GRID_TOP - gridBottom
    return Math.max(3, Math.floor(availableGridHeight / ROW_HEIGHT))
})

// Determine color for a gantt data item based on its cdr_type
function getItemColor (item: GanttDataItem): string {
    const status = item.cdr_type

    if (status.toLowerCase().includes('outgoing')) {
        return colorsForPieChartCallsPerAccColorsLayout.Outgoing
    }

    if (status.toLowerCase().includes('incoming')) {
        return colorsForPieChartCallsPerAccColorsLayout.Incoming
    }

    // Check if there is a matching account break with a custom color
    const accountBreaks = selectedAccountBreaks.value
    const statusByAccount = accountBreaks.find((accountBreak) => accountBreak.Name === status)

    if (statusByAccount?.ColorCode) {
        return statusByAccount.ColorCode
    }

    return colorsForPieChartCallsPerAccColorsLayout.Outgoing
}

// Group gantt data by cdr_type and assign colors
function buildSeriesFromGanttData (ganttData: GanttDataItem[]) {
    const groupedByStatus: Record<string, GanttDataItem[]> = {}

    // Collect unique cdr_types preserving order
    const uniqueTypes: string[] = []
    for (const item of ganttData) {
        if (!groupedByStatus[item.cdr_type]) {
            groupedByStatus[item.cdr_type] = []
            uniqueTypes.push(item.cdr_type)
        }
        groupedByStatus[item.cdr_type].push(item)
    }

    return uniqueTypes.map(cdrType => {
        const items = groupedByStatus[cdrType]
        const color = getItemColor(items[0])

        return {
            name: cdrType,
            type: 'custom',
            // Clip rendered items to the grid area so they don't bleed over the legend
            clip: true,
            renderItem: (params: unknown, api: {
                value: (dim: number) => number
                coord: (value: [number, number]) => number[]
                size: (value: [number, number]) => number[]
                style: (opts?: Record<string, unknown>) => Record<string, unknown>
            }) => {
                const categoryIndex = api.value(0)
                const startTime = api.value(1)
                const endTime = api.value(2)

                const start = api.coord([ startTime, categoryIndex ])
                const end = api.coord([ endTime, categoryIndex ])
                const rowHeight = api.size([ 0, 1 ])[1]
                const height = Math.max(rowHeight * 0.8, 15)

                const rectShape = {
                    x: start[0],
                    y: start[1] - height / 2,
                    width: Math.max(end[0] - start[0], 1),
                    height
                }

                return {
                    type: 'rect',
                    shape: rectShape,
                    style: api.style()
                }
            },
            encode: {
                x: [ 1, 2 ],
                y: 0
            },
            itemStyle: {
                color
            },
            data: items.map(item => ({
                value: [
                    item.y,
                    new Date(item.x).getTime(),
                    new Date(item.x2).getTime()
                ],
                itemStyle: {
                    color: getItemColor(item)
                },
                // Attach metadata for tooltip
                label: item.lable ?? '',
                cdrType: item.cdr_type
            }))
        }
    })
}

// Transform API data to ECharts options
const chartOptions = computed((): ChartOptions => {
    if (!loadedData.value || !Array.isArray(loadedData.value) || loadedData.value.length === 0) {
        return {}
    }

    const apiData = loadedData.value[0]

    if (!apiData?.GanttData || apiData.GanttData.length === 0) {
        return {}
    }

    const allCategories = apiData.categories ?? []

    // Only include categories that are actually referenced by GanttData y-values
    // Highcharts auto-ranges the yAxis to min(y)..max(y), so we replicate that behavior
    const usedYIndices = new Set(apiData.GanttData.map(item => item.y))
    const maxY = Math.max(...usedYIndices)
    const categories = allCategories.slice(0, maxY + 1)

    const series = buildSeriesFromGanttData(apiData.GanttData)

    // Only add scroll controls when there are more categories than fit in the current height
    const needsScroll = categories.length > itemsPerPage.value

    const dataZoom = needsScroll
        ? [
            {
                yAxisIndex: 0,
                filterMode: 'none',
                startValue: 0,
                endValue: itemsPerPage.value - 1,
                zoomLock: true,
                brushSelect: false,
                width: 14,
                right: 4,
                showDetail: false,
            },
            {
                // Mouse-wheel scrolling inside the chart area
                type: 'inside',
                yAxisIndex: 0,
                filterMode: 'none',
                zoomLock: true,
                zoomOnMouseWheel: false,
                moveOnMouseWheel: true
            }
        ]
        : []

    return {
        useUTC: true,
        tooltip: {
            trigger: 'item',
            confine: true,
            formatter: (params: { data?: { label?: string; cdrType?: string; value?: number[] } }) => {
                const data = params?.data
                if (!data?.value) return ''

                const startTime = new Date(data.value[1])
                const endTime = new Date(data.value[2])

                const formatTime = (date: Date) => {
                    return date.toISOString().split('T')[1]?.substring(0, 8) ?? ''
                }

                const label = data.label || ''
                const cdrType = data.cdrType || ''

                return `<span>${label} (${cdrType})</span><br/><span>${formatTime(startTime)} - ${formatTime(endTime)}</span>`
            }
        },
        legend: {
            data: series.map(s => s.name),
            bottom: 10,
            left: 'center',
            itemGap: 10,
            type: series.length > 10 ? 'scroll' : 'plain'
        },
        grid: {
            top: GRID_TOP,
            bottom: '15%',
            left: 160,
            // Extra right margin to accommodate the slider when scrolling is needed
            right: needsScroll ? 30 : 16
        },
        xAxis: {
            type: 'time',
            axisLine: {
                show: true
            },
            axisLabel: {
                formatter: '{HH}:{mm}'
            }
        },
        yAxis: {
            type: 'category',
            data: categories,
            inverse: true,
            axisLine: {
                show: false
            },
            axisTick: {
                show: false
            },
            axisLabel: {
                // Fixed label width keeps the grid stable while scrolling
                width: 140,
                overflow: 'hidden',
            }
        },
        dataZoom,
        series
    }
})
</script>

<style scoped lang="scss">

</style>
